import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Icon } from 'antd';
import PropTypes from 'prop-types';
import ModalConfirm from 'components/Utils/ModalConfirm';
import moment from 'moment';
import CloneSelectionModal from './CloneSelectionModal/CloneSelectionModal';
import './SavedOffersTable.less';

const SavedOffersTable = (props) => {
  const [userRequest, setUserRequest] = useState({
    currentPage: 1,
    flightData: [],
    limit: 10,
    loading: true,
    total: 0,
    sortBy: '',
    order: '',
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [formRef, setFormRef] = useState(null);

  const {
    loading, flightData, limit, currentPage, total,
  } = userRequest;

  const {
    cloneFlight, currentFlights, currentIata, deleteFlight, editFlight, expireFlight, iataList, isRound,
  } = props;

  /**
   * @param {string} text The row value.
   * @param {string} status The offer status to expired.
   * @param {string} className The container name
   * @returns {*}, the row span.
   */
  const renderTextRow = (text = '', status, className) => {
    const classSpanText = status ? 'flight-expired-offer' : `${className}-span-text`;
    const missingPermalink = text === '---' ? 'flight-missing-permalink' : '';
    return (
      <span className={className}>
        <span className={`${text !== '' ? classSpanText : 'text-undefined'} ${missingPermalink}`}>{text === '---' ? '⚠️' : text}</span>
      </span>
    );
  };

  /**
   * Method to edit the selected flight
   * @param rowIndex
   */
  const editSelectedRow = (rowIndex) => {
    editFlight(rowIndex);
  };

  /**
   * @param {number} amount The row value.
   * @param {string} status The offer status to expired.
   * @param {string} className The container name
   * @returns {*}, the row span.
   */
  const renderCurrencyRow = (amount = 0, status, className) => {
    const classSpanText = status ? 'flight-expired-offer' : `${className}-span-text`;
    const currencyAmount = `$${amount.toFixed(0).replace(/\d(?=(\d{3})+)/g, '$&,')}`;
    return (
      <span className={className}>
        <span className={amount ? classSpanText : 'text-undefined'}>{currencyAmount}</span>
      </span>
    );
  };

  /**
   *
   * @param {string} text Fragment of text to be displayed in table
   */
  const handlePermalink = (text) => (text ? `${text.slice(0, 20)}...` : '---');

  // To ignore the sorter and filters on table coverage
  /* istanbul ignore next */
  const columns = [
    {
      title: 'Fecha Salida',
      dataIndex: 'departure_date',
      key: 'departure_date',
      render: (text, record) => renderTextRow(moment.utc(text).local().format('L'), record.expired, 'table-row-flight-departure-date'),
    },
    {
      title: 'Fecha Especial',
      dataIndex: 'special_date',
      key: 'special_date',
      render: (text, record) => renderTextRow(text || '-', record.expired, 'table-row-flight-special-date'),
    },
    {
      title: 'Fin de Semana',
      dataIndex: 'weekend',
      key: 'weekend',
      render: (text, record) => renderTextRow(text ? 'Si' : 'No', record.expired, 'table-row-flight-weekend'),
    },
    {
      title: 'No. Escalas',
      dataIndex: 'connections',
      key: 'connections',
      render: (text, record) => renderTextRow(text, record.expired, 'table-row-flight-scales'),
    },
    {
      title: 'No. Visas',
      dataIndex: 'visas',
      key: 'visas',
      render: (text, record) => renderTextRow(text, record.expired, 'table-row-flight-visas'),
    },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      render: (text, record) => renderCurrencyRow(text, record.expired, 'table-row-flight-price'),
    },
    {
      title: 'Moneda',
      dataIndex: 'currency',
      key: 'currency',
      render: (text, record) => renderTextRow(text, record.expired, 'table-row-flight-currency'),
    },
    {
      title: 'Promo Code',
      dataIndex: 'promo_code',
      key: 'promo_code',
      render: (text, record) => renderTextRow(text || 'N/A', record.expired, 'table-row-flight-promo'),
    },
    {
      title: 'Proveedor',
      dataIndex: 'provider',
      key: 'provider',
      render: (text, record) => renderTextRow(text, record.expired, record, 'table-row-flight-provider'),
    },
    {
      title: 'Permalink',
      dataIndex: 'provider_permalink',
      key: 'provider_permalink',
      render: (text, record) => renderTextRow(handlePermalink(text), record.expired, 'table-row-flight-link'),
    },
    {
      title: 'Editar',
      dataIndex: 'toCheck', // TODO - check post link
      key: 'toCheck',
      render: (_, record, index) => (
        <span>
          <Button onClick={() => editSelectedRow(index)} id={`copy-to-clipboard-button-${record.post_id}`} type="default">
            <Icon type="edit" />
          </Button>
        </span>
      ),
    },
  ];

  if (isRound) {
    columns.splice(1, 0, {
      title: 'Fecha Regreso',
      dataIndex: 'arrival_date',
      key: 'arrival_date',
      render: (text, record) => renderTextRow(moment.utc(text).local().format('L'), record.expired, 'table-row-flight-departure-date'),
    });
  }

  /**
   * use effect (HOOK)
   */
  useEffect(() => {
    setUserRequest({
      currentPage: 1,
      flightData: currentFlights,
      limit: 100,
      loading: false,
      order: '',
      sortBy: '',
      total: 0,
    });
    setSelectedRowKeys([]);
  }, [currentFlights]);

  /**
   * Callback method to handle information when rows are selected in flights table
   * @param {*} rowsKey // array of all keys selected
   * @param {*} rowsInfo // array of all information inside each selected index
   */
  const onSelectChange = (rowsKey /* , rowsInfo */) => {
    // TODO - Use rowsKey & rowsInfo when that data is required in the workflow
    setSelectedRowKeys(rowsKey);
  };

  /**
   * Method to handle row selection in table
   */
  const rowSelection = {
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: record.name === 'Disabled User', // Column configuration not to be checked
      name: record.name,
    }),
    selectedRowKeys,
  };

  /**
   * Method to delete selected rows
   * This calls a received method since data is actually stored in parent component
   */
  const deleteSelectedRows = () => {
    ModalConfirm('Borrar Vuelo', `Estás a punto de borrar ${selectedRowKeys.length} vuelo(s). ¿Deseas continuar?`, () => deleteFlight(selectedRowKeys));
  };

  /**
   * Method to expire selected rows
   * This calls a received method since data is actually stored in parent component
   */
  const expireSelectedRows = () => {
    ModalConfirm('Expirar Vuelo', `Estás a punto de expirar ${selectedRowKeys.length} vuelo(s). ¿Deseas continuar?`, () => expireFlight(selectedRowKeys));
  };

  /**
   * Method to close clone selection Modal
   * It unchecks all boxes
   */
  const closeSelectionModal = () => {
    formRef.resetFields();
    setCloneModalVisible(false);
  };

  /**
   * Callback to execute once user has selected flights to be clones and click on 'OK'
   * This calls `cloneFlight` sending selected flights and departure iatas
   */
  const handleSelectedIatas = () => {
    formRef.validateFields((err, value) => {
      if (err) {
        return;
      }
      closeSelectionModal();
      cloneFlight(selectedRowKeys, value.iatagroup);
    });
  };

  /**
   * This method is required to use a form inside a Modal
   */
  const saveFormRef = useCallback((node) => {
    if (node !== null) {
      setFormRef(node);
    }
  }, []);

  // Columns: the table columns.
  // dataSource: the data from server (STORE).
  // loading: to show the loading spinner.
  return (
    <div className="flights-table-container">
      <div className="flights-table-header">
        <h2 className="flights-table-title">Ofertas Guardadas</h2>
      </div>
      <div className="flights-table-buttons">
        <Button className="table-tool-button expire-button" onClick={expireSelectedRows}>
          Expirar
        </Button>
        <Button className="table-tool-button clone-button" onClick={() => setCloneModalVisible(true)}>
          Clonar
        </Button>
        <Button className="table-tool-button" type="danger" onClick={deleteSelectedRows}>
          Borrar
        </Button>
      </div>
      <CloneSelectionModal
        ref={saveFormRef}
        visible={cloneModalVisible}
        onCancel={closeSelectionModal}
        onAccept={() => handleSelectedIatas()}
        iataList={iataList}
        currentIata={currentIata}
        flightAmount={selectedRowKeys.length}
      />
      <div className="flights-table-content">
        <Table
          rowSelection={rowSelection}
          rowKey={(record) => record.post_id}
          locale={{
            emptyText: 'No hay ofertas guardadas',
          }}
          pagination={{
            showQuickJumper: true,
            current: currentPage,
            total,
            pageSize: limit,
          }}
          columns={columns}
          dataSource={flightData}
          rowClassName={(record, index) => (index % 2 ? 'background-gray' : 'background-white')}
          loading={loading}
        />
      </div>
    </div>
  );
};

SavedOffersTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  currentFlights: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  iataList: PropTypes.array.isRequired,
  cloneFlight: PropTypes.func.isRequired,
  currentIata: PropTypes.string,
  deleteFlight: PropTypes.func.isRequired,
  editFlight: PropTypes.func.isRequired,
  expireFlight: PropTypes.func.isRequired,
  isRound: PropTypes.bool.isRequired,
};

SavedOffersTable.defaultProps = {
  currentFlights: [],
  currentIata: '',
};

export default SavedOffersTable;
